import { Injectable } from '@angular/core';

interface PersistedViewState {
  version: 1;
  lastRoute: string | null;
  tabs: Record<string, string>;
}

@Injectable({
  providedIn: 'root',
})
export class ViewStateService {
  private readonly storageKey = 'kits:view-state';
  private readonly fallbackRoute = '/time';
  private readonly nonRestorableRoutes = new Set(['/','/login','/loginCallback','/logout','/password']);

  getLastRouteOrFallback(): string {
    return this.getLastRoute() || this.fallbackRoute;
  }

  getLastRoute(): string | null {
    const state = this.readState();
    const route = state.lastRoute;
    return route && this.isRestorableRoute(route) ? route : null;
  }

  setLastRoute(route: string): void {
    const normalized = this.normalizeRoute(route);
    if (!this.isRestorableRoute(normalized)) {
      return;
    }

    const state = this.readState();
    state.lastRoute = normalized;
    this.writeState(state);
  }

  getViewTab(viewKey: string): string | null {
    const state = this.readState();
    return state.tabs[viewKey] || null;
  }

  setViewTab(viewKey: string, tabKey: string): void {
    const state = this.readState();
    state.tabs[viewKey] = tabKey;
    this.writeState(state);
  }

  private isRestorableRoute(route: string): boolean {
    return !!route && !this.nonRestorableRoutes.has(route);
  }

  private normalizeRoute(route: string): string {
    if (!route) {
      return this.fallbackRoute;
    }

    const path = route.split('?')[0].split('#')[0];
    if (!path || path === '') {
      return '/';
    }

    return path.endsWith('/') && path !== '/' ? path.slice(0, -1) : path;
  }

  private readState(): PersistedViewState {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return this.emptyState();
    }

    try {
      const raw = window.sessionStorage.getItem(this.storageKey);
      if (!raw) {
        return this.emptyState();
      }

      const parsed = JSON.parse(raw) as Partial<PersistedViewState>;
      if (parsed.version !== 1) {
        return this.emptyState();
      }

      return {
        version: 1,
        lastRoute: typeof parsed.lastRoute === 'string' ? parsed.lastRoute : null,
        tabs: parsed.tabs && typeof parsed.tabs === 'object' ? parsed.tabs : {},
      };
    } catch {
      return this.emptyState();
    }
  }

  private writeState(state: PersistedViewState): void {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return;
    }

    window.sessionStorage.setItem(this.storageKey, JSON.stringify(state));
  }

  private emptyState(): PersistedViewState {
    return {
      version: 1,
      lastRoute: null,
      tabs: {},
    };
  }
}
